import React, { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { createPortal } from 'react-dom';
import { makeStyles } from 'tss-react/mui';

interface BackgroundTaskTrayContextValue {
  container: HTMLElement | null;
}

const BackgroundTaskTrayContext = createContext<BackgroundTaskTrayContextValue | null>(null);

const useStyles = makeStyles()(theme => ({
  tray: {
    '& > *': {
      pointerEvents: 'auto',
    },
    alignItems: 'flex-end',
    bottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    pointerEvents: 'none',
    position: 'fixed',
    right: theme.spacing(2),
    zIndex: theme.zIndex.modal + 1,
  },
}));

/**
 * The bottom-right corner where long-running work reports progress. Panels
 * from anywhere under it stack here in the order they opened, so no feature
 * positions its own floating box.
 */
export function BackgroundTaskTray({ children }: { children: ReactNode }): JSX.Element {
  const { classes } = useStyles();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ container }), [container]);

  return (
    <BackgroundTaskTrayContext.Provider value={value}>
      {children}
      <div
        ref={setContainer}
        aria-label="Background tasks"
        className={classes.tray}
        role="region"
      />
    </BackgroundTaskTrayContext.Provider>
  );
}

/** Render children into the tray, while their state stays with the component that owns the task. */
export function BackgroundTaskTrayPortal({
  children,
}: {
  children: ReactNode;
}): JSX.Element | null {
  const { container } = useBackgroundTaskTray();
  if (!container) return null;

  return createPortal(children, container);
}

function useBackgroundTaskTray(): BackgroundTaskTrayContextValue {
  const context = useContext(BackgroundTaskTrayContext);
  if (!context)
    throw new Error('Background task panels must be rendered inside BackgroundTaskTray.');

  return context;
}
